import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  Input,
  OnChanges,
  SimpleChanges,
  TemplateRef,
  ViewChild
} from '@angular/core';
import { TranslateDirective, TranslateService } from '@ngx-translate/core';

import { ListComponent } from '../../../../../components/list/list.component';
import { ListItemComponent } from '../../../../../components/list/list-item.component';
import { ListItemValueComponent } from '../../../../../components/list/list-item-value.component';
import { ListItemLabelComponent } from '../../../../../components/list/list-item-label.component';

import { DatePipe } from '../../../../../pipes/date.pipe';
import { NumberPipe } from '../../../../../pipes/number.pipe';
import { CurrencyPipe } from '../../../../../pipes/currency.pipe';
import { PercentPipe } from '../../../../../pipes/percent.pipe';
import { PlaceholderPipe } from '../../../../../pipes/placeholder.pipe';

import { DetailedQuote } from '../../../../../domain/public-api';
import { Frequency } from '../../domain/frequency.enum';
import { QuoteIssueType } from '../../domain';
import { SecurityService } from '../../../../../services/security.service';

@Component({
  selector: 'td-wb-detailed-quote-more-details',
  templateUrl: 'detailed-quote-more-details.component.html',
  styleUrls: ['detailed-quote-more-details.component.scss'],
  imports: [
    NgTemplateOutlet,
    TranslateDirective,
    ListComponent,
    ListItemComponent,
    ListItemLabelComponent,
    ListItemValueComponent,
    DatePipe,
    NumberPipe,
    CurrencyPipe,
    PercentPipe,
    PlaceholderPipe
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DetailedQuoteMoreDetailsComponent implements OnChanges {

  @Input() quote: DetailedQuote;

  @ViewChild('stock', { static: true }) templateRefStock: TemplateRef<unknown>;
  @ViewChild('index', { static: true }) templateRefIndex: TemplateRef<unknown>;
  @ViewChild('mf', { static: true }) templateRefMf: TemplateRef<unknown>;
  @ViewChild('option', { static: true }) templateRefOption: TemplateRef<unknown>;
  @ViewChild('etf', { static: true }) templateRefEtf: TemplateRef<unknown>;

  currentTemplate: TemplateRef<unknown>;
  readonly placeholder: string = '\u2014';

  private readonly translateService: TranslateService = inject(TranslateService);
  private readonly securityService: SecurityService = inject(SecurityService);

  /**
   * Set template on quote changes
   */
  ngOnChanges(changes: SimpleChanges): void {
    const change = changes['quote'];
    if (change !== undefined) {
      this.currentTemplate = this.getIssueTypeTemplate();
    }
  }

  /**
   * This function returns frequency header text based on dividend frequency
   *
   * @return
   */
  getDividendFrequencyHeader(): string {
    let frequency = '';

    switch (this.quote.dividend.frequency) {
      case Frequency.IRREGULAR:
      case Frequency.MONTHLY:
      case Frequency.QUARTERLY:
      case Frequency.BIANNUAL:
      case Frequency.TRIANNUAL:
      case Frequency.YEARLY:
        frequency = this.quote.dividend.frequency.toUpperCase() + '_';
        break;
    }

    frequency = this.translateService.instant('tdWb.markets.quote.' + frequency + 'DIVIDEND');
    frequency += '/';
    frequency += this.translateService.instant('tdWb.markets.quote.YIELD');
    return frequency;
  }

  getLoadType(): string {
    switch  (this.quote.loadType) {
      case 'BE':
      case 'DM':
      case 'DO':
      case 'FE':
      case 'IS':
      case 'LL':
      case 'NL':
      case 'SL':
      case 'VS':
        return this.translateService.instant('tdWb.markets.quote.mf.LOAD_TYPE_' + this.quote.loadType);
    }
    return undefined;
  }

  userHasDripPermission(): boolean {
    return this.securityService.hasPermission('DIVIDEND_REINVESTMENT_PLAN');
  }

  /**
   * get template based on issue type
   *
   * @returns template to be used in template outlet
   */
  private getIssueTypeTemplate(): TemplateRef<unknown> | undefined {
    switch (this.quote.issueType) {
      case QuoteIssueType.STOCK:
        return this.templateRefStock;
      case QuoteIssueType.ETF:
        return this.templateRefEtf;
      case QuoteIssueType.INDEX:
        return this.templateRefIndex;
      case QuoteIssueType.OPTION:
        return this.templateRefOption;
      case QuoteIssueType.MF:
        return this.templateRefMf;
      default:
        return undefined;
    }
  }
}
